import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import { db } from '../../lib/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

interface Book {
  id: string;
  title: string;
  author: string;
  fileUrl: string;
  progress: number;
}

const Reader: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const { user } = useAuth();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBook = async () => {
      if (!user || !bookId) return;

      try {
        const bookDoc = await getDoc(doc(db, 'books', bookId));
        if (bookDoc.exists()) {
          setBook({ id: bookDoc.id, ...bookDoc.data() } as Book);
        } else {
          setError('Book not found');
        }
      } catch (err) {
        setError('Error fetching book');
        console.error('Error fetching book:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [user, bookId]);

  const updateProgress = async (newProgress: number) => {
    if (!book || !user) return;

    try {
      await updateDoc(doc(db, 'books', book.id), {
        progress: newProgress
      });
    } catch (err) {
      console.error('Error updating progress:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600">{error || 'Book not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{book.title}</h1>
          <p className="text-lg text-gray-600">{book.author}</p>
        </div>

        {/* Reading Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Reading Progress</span>
            <span className="text-sm text-gray-500">{book.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{ width: `${book.progress}%` }}
            ></div>
          </div>
        </div>

        {/* Book Content */}
        <div className="bg-white rounded-lg shadow p-6">
          <iframe
            src={book.fileUrl}
            className="w-full h-[600px] border-0"
            title={book.title}
          />
        </div>

        {/* Controls */}
        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={() => updateProgress(Math.max(0, book.progress - 10))}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Previous Chapter
          </button>
          <button
            onClick={() => updateProgress(Math.min(100, book.progress + 10))}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Next Chapter
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reader; 